import { allowCookies, optimizelySDKKey } from 'utils/settings';
import { experiments } from './experiments';
import { FeatureName } from './features';
import { getCookie } from 'utils/cookies';
import { logEvent } from 'helpers/analytics';

let optimizelyClientInstance: any;

export let activeExperiments: {
    [key: string]: string | null;
} = {};

/**
 * Gets the user ID from the '_ga' cookie.
 * Overrides it with the value of 'optimizely_user' if found in the query parameters.
 */
export const getUserId = () => {
    const userId = getCookie('_ga');
    const userIdOverride = new URLSearchParams(window.location.search).get(
        'optimizely_user'
    );
    return userIdOverride || userId;
};

export const getAttributes = () => {
    return {
        market: window?.location?.pathname?.split('/')?.[1].toUpperCase() || '',
        isLoggedIn: !!getCookie('idp_reguser') || false,
    };
};

export const shouldAllowOptimizely = () => {
    if (!optimizelySDKKey) {
        return false;
    }
    if (experiments.length === 0) {
        return false;
    }
    if (window.location.hostname === 'localhost') {
        return true;
    }
    return allowCookies() && !!getUserId();
};

const logExperimentActivation = (activateObject: any) => {
    const experimentId = activateObject?.experiment?.id ?? '';
    const variationId = activateObject?.variation?.id ?? '';

    // log Optimizely health metrics event
    track('step 3 - logOptimizelyExperimentActivation');

    // log event to Google Analytics
    logEvent(
        'optimizely',
        'user bucketed',
        `(${experimentId}):(${variationId})`
    );
};

export const initializeOptimizely = () => {
    if (shouldAllowOptimizely()) {
        const sdk = window?.ikea?.navigation?.optimizely?.sdk;
        const datafile =
            window?.ikea?.navigation?.optimizely?.dataFiles?.[
                optimizelySDKKey!
            ];

        if (sdk && datafile) {
            optimizelyClientInstance = sdk.createInstance({ datafile });
            optimizelyClientInstance?.notificationCenter?.addNotificationListener(
                window?.ikea?.navigation?.optimizely?.sdk?.enums
                    .NOTIFICATION_TYPES.ACTIVATE,
                logExperimentActivation
            );
            activateExperiments();
        }
    }
};

/**
 * Activates a single experiment.
 *
 * @param experimentName
 */
export const activateExperiment = (experimentName: string) => {
    if (!optimizelyClientInstance || activeExperiments[experimentName]) {
        return;
    }
    activeExperiments[experimentName] = optimizelyClientInstance.activate(
        experimentName,
        getUserId(),
        getAttributes()
    );
};
/**
 * Activates experiments where 'activateOnPageLoad' is 'true' on page load.
 */
export const activateExperiments = () => {
    if (!optimizelyClientInstance || experiments.length === 0) {
        return;
    }
    experiments.forEach(experiment => {
        if (experiment.activateOnPageLoad) {
            activateExperiment(experiment.name);
        }
    });
};

/**
 * Returns the variation where visitor will be bucketed.
 * If the visitor is in the experiment, this function returns the variation name.
 * If the visitor is NOT in the experiment, this function returns 'null'.
 *
 * @param experimentName
 * @returns { string | null }
 */
export const getVariation = (experimentName: string) =>
    optimizelyClientInstance?.getVariation(
        experimentName,
        getUserId(),
        getAttributes()
    ) ?? null;

/**
 * Returns whether 'featureName' is enabled.
 *
 * @param featureName
 * @returns { boolean }
 */
export const isFeatureEnabled = (featureName: FeatureName) =>
    optimizelyClientInstance?.isFeatureEnabled(
        featureName,
        getUserId(),
        getAttributes()
    ) ?? false;

/**
 * Tracks 'eventKey'.
 * Optional: Custom tags.
 *
 * @param eventKey
 * @param tags
 */
export const track = (
    eventKey: string,
    tags: { [key: string]: string | number | boolean } = {}
) =>
    optimizelyClientInstance?.track(
        eventKey,
        getUserId(),
        getAttributes(),
        tags
    );
